/**
 * Per-section layout width / height helpers for the section-grid
 * rendering mode.
 *
 * Values are stored inside the existing `PageSection.config` JSON blob
 * (no backend/schema changes required). Missing values default to `full`
 * width and `1` row, preserving today's single-column stack behavior.
 *
 * Width system: numeric column spans (1–5) + 'full' (span all).
 * Legacy named widths ('two-thirds', 'half', 'third') are auto-converted.
 */

// Legacy named widths (stacked-layout fallback)
export const SECTION_WIDTHS_LEGACY = ['full', 'two-thirds', 'half', 'third'];

// New: valid widths are 'full' or any integer 1–5
export const SECTION_WIDTHS = ['full', 1, 2, 3, 4, 5];
export const SECTION_HEIGHTS = [1, 2];

export const DEFAULT_WIDTH = 'full';
export const DEFAULT_HEIGHT = 1;

/**
 * Build the list of width options for a given grid column count.
 * Returns ['full', 1, 2, ..., gridColumns].
 */
export function getColumnSpanOptions(gridColumns) {
  if (!gridColumns || gridColumns < 1) return ['full'];
  const opts = ['full'];
  for (let i = 1; i <= gridColumns; i++) opts.push(i);
  return opts;
}

/**
 * Get a display label for a width value.
 * Numeric values show as the number; 'full' shows as 'Full'.
 */
export function getWidthLabel(width) {
  if (width === 'full') return 'Full';
  const n = Number(width);
  if (!isNaN(n) && n >= 1) return String(n);
  // Legacy fallback
  return WIDTH_FALLBACK_LABELS_LEGACY[width] || String(width);
}

/**
 * Get an i18n key for a width value (for use with t()).
 * Numeric widths return the number directly (no translation needed).
 */
export function getWidthLabelKey(width) {
  if (width === 'full') return 'admin.sections.width.full';
  const n = Number(width);
  if (!isNaN(n) && n >= 1) return String(n);
  // Legacy named widths
  return WIDTH_LABEL_KEYS[width] || String(width);
}

/**
 * Get an i18n key for a height value (for use with t()).
 */
export function getHeightLabelKey(height) {
  return HEIGHT_LABEL_KEYS[height] || HEIGHT_FALLBACK_LABELS[height] || String(height);
}

/**
 * Normalize a stored width value. Converts legacy named widths to numeric
 * based on the current grid column count. Pass gridColumns=null for
 * stacked (non-grid) mode — returns the raw value unchanged.
 */
export function normalizeWidth(width, gridColumns) {
  if (width === 'full' || width === undefined || width === null) return 'full';
  // Already numeric
  const n = Number(width);
  if (!isNaN(n) && n >= 1) return Math.min(n, gridColumns || n);
  // Legacy named → numeric conversion (requires grid column count)
  if (gridColumns) {
    switch (width) {
      case 'two-thirds': return Math.max(1, Math.ceil(gridColumns * 2 / 3));
      case 'half':       return Math.max(1, Math.ceil(gridColumns / 2));
      case 'third':      return Math.max(1, Math.ceil(gridColumns / 3));
    }
  }
  return 'full';
}

// Legacy i18n keys (kept for stacked mode fallback)
export const WIDTH_LABEL_KEYS = {
  full: 'admin.sections.width.full',
  'two-thirds': 'admin.sections.width.twoThirds',
  half: 'admin.sections.width.half',
  third: 'admin.sections.width.third'
};

const WIDTH_FALLBACK_LABELS_LEGACY = {
  full: 'Full',
  'two-thirds': '2/3',
  half: '1/2',
  third: '1/3'
};

export const WIDTH_FALLBACK_LABELS = {
  full: 'Full',
  'two-thirds': '2/3',
  half: '1/2',
  third: '1/3'
};

export const HEIGHT_LABEL_KEYS = {
  1: 'admin.sections.height.short',
  2: 'admin.sections.height.tall'
};

export const HEIGHT_FALLBACK_LABELS = {
  1: '1 row',
  2: '2 rows'
};

export function getSectionWidth(section) {
  const w = section?.config?.width;
  if (w === 'full') return 'full';
  const n = Number(w);
  if (!isNaN(n) && n >= 1 && n <= 5) return n;
  // Legacy named widths
  if (SECTION_WIDTHS_LEGACY.includes(w)) return w;
  return DEFAULT_WIDTH;
}

export function getSectionHeight(section) {
  const h = Number(section?.config?.height);
  return SECTION_HEIGHTS.includes(h) ? h : DEFAULT_HEIGHT;
}

/**
 * Map a width value to Tailwind col-span classes (stacked/SectionGrid mode).
 * Mobile (default) is full width; md+ respects the configured width.
 * Handles both numeric column spans and legacy named widths.
 */
export function widthToColSpan(width) {
  // Numeric column spans → map to 12-col grid
  const n = Number(width);
  if (!isNaN(n) && n >= 1) {
    if (n >= 4) return 'col-span-1 md:col-span-12';       // 4-5 cols = full
    if (n === 3) return 'col-span-1 md:col-span-8';       // 3 cols ≈ 2/3
    if (n === 2) return 'col-span-1 md:col-span-6';       // 2 cols = 1/2
    return 'col-span-1 md:col-span-6 lg:col-span-4';      // 1 col ≈ 1/3
  }
  // Legacy named widths
  switch (width) {
    case 'two-thirds':
      return 'col-span-1 md:col-span-8';
    case 'half':
      return 'col-span-1 md:col-span-6';
    case 'third':
      return 'col-span-1 md:col-span-6 lg:col-span-4';
    case 'full':
    default:
      return 'col-span-1 md:col-span-12';
  }
}

/**
 * Map a height value to Tailwind row-span + min-height classes.
 * Only active on md+ screens (mobile stays auto-height).
 *
 * `row-span-2` tells CSS Grid to span 2 rows, allowing adjacent 1-row
 * sections to flow into the empty space beside the tall section.
 * `min-h` keeps a visual height minimum in non-grid contexts.
 */
export function heightToRowSpan(height) {
  switch (Number(height)) {
    case 2:
      return 'md:row-span-2 md:min-h-[32rem]';
    case 1:
    default:
      return '';
  }
}

export function getSectionGridClasses(section) {
  return `${widthToColSpan(getSectionWidth(section))} ${heightToRowSpan(getSectionHeight(section))}`.trim();
}

/**
 * Width/height classes for NON-grid (stacked) layouts.
 * Uses max-width + mx-auto to constrain sections within their container.
 * Only applies on md+ screens — mobile is always full width.
 */
const STACKED_WIDTH_CLASSES = {
  full: 'col-span-full',
  'two-thirds': 'col-span-full md:col-span-8',
  half: 'col-span-full md:col-span-6',
  third: 'col-span-full md:col-span-4',
};

// Numeric column spans for stacked mode (maps to 12-col Tailwind grid)
function getStackedWidthClass(width) {
  if (typeof width === 'string' && STACKED_WIDTH_CLASSES[width]) {
    return STACKED_WIDTH_CLASSES[width];
  }
  const n = Number(width);
  if (!isNaN(n) && n >= 1) {
    if (n >= 4) return 'col-span-full';
    if (n === 3) return 'col-span-full md:col-span-8';
    if (n === 2) return 'col-span-full md:col-span-6';
    return 'col-span-full md:col-span-4';
  }
  return 'col-span-full';
}

const STACKED_HEIGHT_CLASSES = {
  1: '',
  2: 'md:row-span-2 md:min-h-[32rem]',
};

export function getSectionStretch(section) {
  return section?.config?.stretch === true;
}

/* ── Spacer height helpers ─────────────────────────────────────────── */

export const SPACER_HEIGHTS = ['xs', 'sm', 'md', 'lg', 'xl'];
export const DEFAULT_SPACER_HEIGHT = 'md';

export const SPACER_HEIGHT_LABELS = {
  xs: 'XS',
  sm: 'SM',
  md: 'MD',
  lg: 'LG',
  xl: 'XL',
};

export const SPACER_HEIGHT_VALUES = {
  xs: '1rem',
  sm: '2rem',
  md: '4rem',
  lg: '8rem',
  xl: '12rem',
};

export function getSpacerHeight(section) {
  const h = section?.config?.spacerHeight;
  return SPACER_HEIGHTS.includes(h) ? h : DEFAULT_SPACER_HEIGHT;
}

export function isSpacerSection(sectionType) {
  return typeof sectionType === 'string' && sectionType.startsWith('spacer_');
}

/* ── Content Block helpers ─────────────────────────────────────────── */

export function isContentBlockSection(sectionType) {
  return typeof sectionType === 'string' && sectionType.startsWith('content_block_');
}

export const CONTENT_BLOCK_COLORS = [
  { name: 'Navy',         hex: '#1a1a2e', textColor: '#ffffff' },
  { name: 'Dark Blue',    hex: '#16213e', textColor: '#ffffff' },
  { name: 'Royal Blue',   hex: '#0f3460', textColor: '#ffffff' },
  { name: 'Red',          hex: '#c0392b', textColor: '#ffffff' },
  { name: 'Gold',         hex: '#d4a017', textColor: '#1a1a2e' },
  { name: 'Forest Green', hex: '#1b4332', textColor: '#ffffff' },
  { name: 'Purple',       hex: '#5b2c6f', textColor: '#ffffff' },
  { name: 'Charcoal',     hex: '#2d2d2d', textColor: '#ffffff' },
];

export const CONTENT_BLOCK_GRADIENTS = [
  { name: 'Ocean',    gradient: 'linear-gradient(135deg, #0f3460, #16213e)', textColor: '#ffffff' },
  { name: 'Sunset',   gradient: 'linear-gradient(135deg, #c0392b, #d4a017)', textColor: '#ffffff' },
  { name: 'Forest',   gradient: 'linear-gradient(135deg, #1b4332, #2d6a4f)', textColor: '#ffffff' },
  { name: 'Royal',    gradient: 'linear-gradient(135deg, #5b2c6f, #0f3460)', textColor: '#ffffff' },
  { name: 'Midnight', gradient: 'linear-gradient(135deg, #1a1a2e, #2d2d2d)', textColor: '#ffffff' },
  { name: 'Fire',     gradient: 'linear-gradient(135deg, #c0392b, #5b2c6f)', textColor: '#ffffff' },
  { name: 'Aurora',   gradient: 'linear-gradient(135deg, #16213e, #1b4332)', textColor: '#ffffff' },
  { name: 'Ember',    gradient: 'linear-gradient(135deg, #d4a017, #c0392b)', textColor: '#ffffff' },
];

export const CONTENT_BLOCK_TEXT_SIZES = {
  sm:  { label: 'S',  className: 'text-sm' },
  md:  { label: 'M',  className: 'text-base' },
  lg:  { label: 'L',  className: 'text-lg' },
  xl:  { label: 'XL', className: 'text-2xl' },
};

export const CONTENT_BLOCK_TEXT_ALIGNS = {
  left:   { label: '◀', className: 'text-left' },
  center: { label: '●', className: 'text-center' },
  right:  { label: '▶', className: 'text-right' },
};

export const DEFAULT_CONTENT_BLOCK_BG_TYPE = 'solid';
export const DEFAULT_CONTENT_BLOCK_BG_COLOR = '#1a1a2e';
export const DEFAULT_CONTENT_BLOCK_BG_GRADIENT = 'linear-gradient(135deg, #0f3460, #16213e)';
export const DEFAULT_CONTENT_BLOCK_TEXT_COLOR = '#ffffff';
export const DEFAULT_CONTENT_BLOCK_TEXT = '';
export const DEFAULT_CONTENT_BLOCK_TEXT_ALIGN = 'center';
export const DEFAULT_CONTENT_BLOCK_TEXT_SIZE = 'md';

/* ── Content Block height helpers ──────────────────────────────────── */

export const CONTENT_BLOCK_HEIGHTS = ['sm', 'md', 'lg', 'xl'];
export const DEFAULT_CONTENT_BLOCK_HEIGHT = 'md';

export const CONTENT_BLOCK_HEIGHT_LABELS = {
  sm: 'S',
  md: 'M',
  lg: 'L',
  xl: 'XL',
};

export const CONTENT_BLOCK_HEIGHT_VALUES = {
  sm: '4rem',
  md: '10rem',
  lg: '18rem',
  xl: '28rem',
};

export function getContentBlockHeight(section) {
  const h = section?.config?.blockHeight;
  return CONTENT_BLOCK_HEIGHTS.includes(h) ? h : DEFAULT_CONTENT_BLOCK_HEIGHT;
}

export function getContentBlockBgType(section) {
  const t = section?.config?.bgType;
  return t === 'gradient' ? 'gradient' : 'solid';
}

export function getContentBlockBgColor(section) {
  return section?.config?.bgColor || DEFAULT_CONTENT_BLOCK_BG_COLOR;
}

export function getContentBlockBgGradient(section) {
  return section?.config?.bgGradient || DEFAULT_CONTENT_BLOCK_BG_GRADIENT;
}

export function getContentBlockBackground(section) {
  const bgType = getContentBlockBgType(section);
  return bgType === 'gradient'
    ? getContentBlockBgGradient(section)
    : getContentBlockBgColor(section);
}

export function getContentBlockTextColor(section) {
  return section?.config?.textColor || DEFAULT_CONTENT_BLOCK_TEXT_COLOR;
}

export function getContentBlockText(section) {
  return section?.config?.text ?? DEFAULT_CONTENT_BLOCK_TEXT;
}

export function getContentBlockTextAlign(section) {
  const a = section?.config?.textAlign;
  return CONTENT_BLOCK_TEXT_ALIGNS[a] ? a : DEFAULT_CONTENT_BLOCK_TEXT_ALIGN;
}

export function getContentBlockTextSize(section) {
  const s = section?.config?.textSize;
  return CONTENT_BLOCK_TEXT_SIZES[s] ? s : DEFAULT_CONTENT_BLOCK_TEXT_SIZE;
}

/* ── Section size classes ─────────────────────────────────────────── */

export function getSectionSizeClasses(section, { stretch = false } = {}) {
  const w = getSectionWidth(section);
  const h = getSectionHeight(section);
  const widthCls = getStackedWidthClass(w);
  // When stretch is ON, skip row-span so both items stay in the SAME
  // grid row — items-stretch then equalises their heights.  Keep the
  // min-height so the tall section still drives the row height.
  const heightCls = (stretch && h === 2)
    ? 'md:min-h-[32rem]'
    : (STACKED_HEIGHT_CLASSES[h] || '');
  return `${widthCls} ${heightCls}`.trim();
}
